import { action, computed, observable } from 'mobx'
import { func, validationContext } from 'validx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type ConsultationIncompleteReason } from '@/features/jobs/consultations/Consultation'

/**
 * Asks why a consultation is being completed without a call.
 */
export class IncompleteConsultationCompletionDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The currently selected reason.
   */
  @observable
  accessor reason: ConsultationIncompleteReason | undefined = undefined

  /**
   * The custom reason the consultation was not completed.
   */
  @observable
  accessor otherText = ''

  /**
   * Validation rules for the incomplete consultation completion dialog form.
   */
  validation = validationContext(this, {
    reason: [
      func(() => this.reason !== undefined, 'You must select a reason.'),
    ],
    otherText: [
      func(
        () => this.reason !== 'Other' || this.otherText.trim().length > 0,
        'If you select "Other", you must provide your own reason.',
      ),
    ],
  })

  constructor(
    private readonly _requestCloseWithIncompleteConsultation: (
      consultationIncompleteReason: ConsultationIncompleteReason,
      consultationIncompleteOtherReason?: string,
    ) => Promise<void>,
  ) {}

  /**
   * Whether the reason form is valid.
   */
  @computed
  get isValid() {
    return this.validation.isValid
  }

  /**
   * Sets the reason.
   */
  @action.bound
  setReason(reason?: ConsultationIncompleteReason) {
    this.reason = reason
    this._validate()
  }

  /**
   * Sets the custom reason.
   */
  @action.bound
  setOtherText(otherText: string) {
    this.otherText = otherText
    this._validate()
  }

  /**
   * Resets the reason form.
   */
  @action.bound
  reset() {
    this.reason = undefined
    this.otherText = ''
  }

  /**
   * Submits the reason and completes the job.
   */
  @action.bound
  submit() {
    if (!this.isValid || !this.reason) {
      return
    }

    // The close request alerts on failure; the dialog closes either way.
    void this._requestCloseWithIncompleteConsultation(
      this.reason,
      this.otherText,
    ).catch(() => undefined)
    this.close()
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this._validate()
    this.modal.show()
  }

  /**
   * Closes the dialog, discarding the reason.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  private _validate() {
    this.validation.reset().validate()
  }
}
